import { ArrowLeft } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import type { GetStaticPaths, GetStaticProps } from "next"
import Container from "@/components/common/Container"
import Layout from "@/components/common/Layout"
import MarketingHeading from "@/components/common/MarketingHeading"
import MDXRenderer from "@/components/utils/mdx/MDXRenderer"
import { getAllJobPostingsMeta, getJobPostingBySlug, type JobPosting } from "@/utils/careers-mdx"

interface JobListingPageProps {
  jobPosting: JobPosting | null
}

export const getStaticPaths: GetStaticPaths = async () => {
  const jobPostings = getAllJobPostingsMeta()

  return {
    paths: jobPostings.map((posting) => ({
      params: { slug: posting.slug },
    })),
    fallback: false, // With static export, all paths must be known at build time
  }
}

export const getStaticProps: GetStaticProps<JobListingPageProps> = async ({ params }) => {
  const slug = params?.slug as string
  const jobPosting = await getJobPostingBySlug(slug)

  if (!jobPosting) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      jobPosting,
    },
  }
}

const JobListingPage = ({ jobPosting }: JobListingPageProps) => {
  if (!jobPosting) {
    return <div>Job posting not found</div>
  }

  return (
    <Layout>
      <div className="py-12 max-w-[860px] mx-auto">
        <Container>
          <LinkButton
            href="/careers"
            variant="ghost"
            className="inline-flex mb-12 text-fg-secondary"
          >
            <Icon as={ArrowLeft} />
            Back
          </LinkButton>

          <div className="flex flex-col items-start gap-0 mb-6">
            <MarketingHeading level={1} className="text-3xl md:text-5xl mb-4">
              {jobPosting.title}
            </MarketingHeading>
            <p className="text-lg text-fg-secondary mb-5">{jobPosting.description}</p>
            <p className="text-fg-secondary mb-5">
              {jobPosting.location} • {jobPosting.type || "Full-time"}
            </p>
          </div>

          <MDXRenderer mdxSource={jobPosting.mdxSource} sharedContent={jobPosting.sharedContent} />
        </Container>
      </div>
    </Layout>
  )
}

export default JobListingPage
